/**
 * useLatestRequestQueue
 *
 * Runs one request per item with at most `concurrency` in flight, and keeps
 * only the latest batch: starting a new batch (or calling cancel) aborts the
 * previous one. Its queued items never start, its in-flight requests get their
 * AbortSignal aborted, and their results are never delivered.
 *
 * Built for the label previews (SV-551): one preview request per label, re-run
 * when the printer changes. Each print dialog uses its own queue.
 *
 * Usage:
 *   const queue = useLatestRequestQueue({ concurrency: 4 });
 *   await queue.run(labels, (label, i, signal) => previewLabel(..., { signal }), (i, outcome) => {
 *     previews.value[i] = outcome.ok ? toPreview(outcome.value) : errorPreview();
 *   });
 */
import { getCurrentScope, onScopeDispose } from 'vue';

export type RequestOutcome<R> = { ok: true; value: R } | { ok: false; error: unknown };

export interface LatestRequestQueue {
  /**
   * Start a batch, superseding any running one. `onSettled` is called once per
   * item, only while this batch is still the latest. Resolves when the batch has
   * finished or been superseded; returns false if it was superseded.
   */
  run<T, R>(
    items: readonly T[],
    task: (item: T, index: number, signal: AbortSignal) => Promise<R>,
    onSettled: (index: number, outcome: RequestOutcome<R>) => void,
  ): Promise<boolean>;
  /** Abort the running batch, if any. */
  cancel(): void;
}

export function useLatestRequestQueue(options: { concurrency: number }): LatestRequestQueue {
  const concurrency = Math.max(1, Math.floor(options.concurrency));
  let current: AbortController | null = null;

  function cancel(): void {
    current?.abort();
    current = null;
  }

  async function run<T, R>(
    items: readonly T[],
    task: (item: T, index: number, signal: AbortSignal) => Promise<R>,
    onSettled: (index: number, outcome: RequestOutcome<R>) => void,
  ): Promise<boolean> {
    cancel();
    const controller = new AbortController();
    current = controller;
    const { signal } = controller;
    const queue = items.slice();
    let next = 0;

    async function worker(): Promise<void> {
      // Stop taking items once superseded, so queued requests never start.
      while (!signal.aborted && next < queue.length) {
        const index = next++;
        let outcome: RequestOutcome<R>;
        try {
          outcome = { ok: true, value: await task(queue[index], index, signal) };
        } catch (error) {
          outcome = { ok: false, error };
        }
        if (!signal.aborted) onSettled(index, outcome);
      }
    }

    await Promise.all(Array.from({ length: Math.min(concurrency, queue.length) }, worker));
    if (current === controller) current = null;
    return !signal.aborted;
  }

  // Abort whatever is running when the owning component goes away.
  if (getCurrentScope()) onScopeDispose(cancel);

  return { run, cancel };
}
